import { FC, HTMLAttributes, LazyExoticComponent } from 'react';

import { cva } from 'class-variance-authority';
import { startCase } from 'lodash';
import { cn } from 'utils/helpers';
import { STATUS_TYPE } from 'utils/constants';
import type { IconType } from 'react-icons';
import { GenericIconProps } from 'ui/components/icon/Icon';

const iconClasses = cva([], {
  variants: {
    iconSize: {
      md: ['w-4 h-4'],
      sm: ['w-3 h-3'],
    },
  },
  defaultVariants: {
    iconSize: 'sm',
  },
});

const status = cva(
  [
    'inline-flex',
    'items-center',
    'gap-2',
    'border text-[12px]',
    'rounded',
    'px-2 py-0.5',
    'font-semibold',
    'border-secondary-base',
    'text-secondary-base',
    'w-max',
  ],
  {
    variants: {
      type: {
        success: ['text-primary-base', 'border-primary-base'],
        'on-going': ['text-info-base', 'border-info-base'],
        open: ['text-open-base', 'border-open-base'],
        urgent: ['text-danger-base', 'border-danger-base'],
        cancelled: ['text-danger-base', 'border-danger-base'],
        close: ['text-secondary-light', 'border-secondary-light'],
        warning: ['text-warning-base', 'border-warning-base'],
        progress: ['text-progress-base', 'border-progress-base'],
        pending: ['text-warning-light', 'border-warning-light'],
        delay: ['text-[#7740F4]', 'border-[#7740F4]'],
        default: ['text-secondary-dark', 'border-secondary-dark'],
      },
    },
    defaultVariants: {
      type: STATUS_TYPE.Success,
    },
  },
);

type PROPS = HTMLAttributes<HTMLDivElement> & {
  type?: STATUS_TYPE;
  iconLeft?:
    | React.ComponentType<GenericIconProps>
    | IconType
    | LazyExoticComponent<FC<GenericIconProps>>;
  iconRight?:
    | React.ComponentType<GenericIconProps>
    | IconType
    | LazyExoticComponent<FC<GenericIconProps>>;
  label?: string;
};

const Status: FC<PROPS> = ({
  type,
  className,
  label,
  iconLeft: IconLeft,
  iconRight: IconRight,
  ...props
}) => (
  <div
    className={cn(
      status({
        type,
        className,
      }),
    )}
    {...props}
  >
    {IconLeft && <IconLeft className={cn(iconClasses())} />}
    {label || startCase(type ?? '')}
    {IconRight && <IconRight className={cn(iconClasses())} />}
  </div>
);

export default Status;
